import React, { useContext, useEffect, useLayoutEffect } from 'react';
import { Card, CardBody, CardImg, CardText, CardTitle, Col, Row } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { AchievementsSummaryContext, ResponsiveImageSet as KeyVisual } from '@core/ui';
import { CampaignMetrics } from '@core/frontend/components';
import KeyVisualXs from '@core/assets/images/keyvisual-xs-rgb.webp';
import KeyVisualLg from '@core/assets/images/keyvisual-lg-rgb.webp';
import { sendVirtualPageView } from '../../shared/tracking.js';
import { useProduct } from '../../shared/productContext.jsx';

/**
 * Übersicht der Errungenschaften.
 *
 * Die Errungenschaften sind Teilhabe-Gamification (Teilen, Weiterempfehlen) und damit nicht an
 * das Fördermodell gebunden: die Daten kommen über `AchievementsSummaryContext`, den die
 * App-Shell speist. Die Grafiken je Errungenschaft sind Produkt-Assets und kommen als URLs aus
 * dem Deskriptor (`achievementBadges[type] = { icon, outline }`).
 */
const AchievementsOverview = () => {
    const location = useLocation();
    const { achievementBadges = {} } = useProduct();
    const achievements = useContext(AchievementsSummaryContext);

    useEffect(() => {
        sendVirtualPageView('Errungenschaften');
    }, []);

    useLayoutEffect(() => {
        if (location.hash) {
            const scrollToAnchor = () => {
                const anchorId = location.hash.substring(1);
                const anchorElement = document.getElementById(anchorId);

                if (anchorElement) {
                    anchorElement.scrollIntoView({ behavior: 'smooth' });
                }
            };

            const timeoutId = setTimeout(scrollToAnchor, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [location.hash]);

    return (
        <>
            <Helmet>
                <title>{`${process.env.REACT_APP_PROJECT_TITLE}: Errungenschaften`}</title>
            </Helmet>
            <Row>
                <Col className="px-0">
                    <KeyVisual imageXs={KeyVisualXs} imageLg={KeyVisualLg} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <CampaignMetrics />
                </Col>
            </Row>
            <Row className="my-3">
                <Col>
                    <h1>Errungenschaften</h1>
                </Col>
            </Row>
            <Row className="row-cols-1 row-cols-md-1 g-4">
                {achievements &&
                    Object.entries(achievements).map(([type, achievement]) => {
                        const badge = achievementBadges[type];
                        const badgeSrc = achievement.hasAchieved ? badge?.icon : badge?.outline;

                        return (
                            <Col key={type}>
                                <Card className="mb-3 h-100" id={type}>
                                    <Row className="g-0 h-100">
                                        <Col xs={2} className="d-flex align-items-center justify-content-center">
                                            {badgeSrc && (
                                                <CardImg
                                                    src={badgeSrc}
                                                    className="img-fluid"
                                                    style={{ height: 'auto', maxWidth: '46px', maxHeight: '46px' }}
                                                    alt="Vorschau der Errungenschaft"
                                                />
                                            )}
                                        </Col>
                                        <Col xs={10}>
                                            <CardBody>
                                                <CardTitle tag="h4">{achievement.name}</CardTitle>
                                                <CardText>{achievement.description}</CardText>
                                                <CardText>
                                                    <small className="text-primary">
                                                        {achievement.hasAchieved === 0 ? (
                                                            <>
                                                                Wurde bisher von <strong>keinem Projekt</strong>{' '}
                                                                erreicht!
                                                            </>
                                                        ) : (
                                                            <>
                                                                Wurde bisher von{' '}
                                                                <strong>{achievement.hasAchieved}</strong>{' '}
                                                                {achievement.hasAchieved === 1 ? (
                                                                    <strong>Projekt</strong>
                                                                ) : (
                                                                    <strong>Projekten</strong>
                                                                )}{' '}
                                                                erreicht!
                                                            </>
                                                        )}
                                                    </small>
                                                </CardText>
                                            </CardBody>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        );
                    })}
            </Row>
        </>
    );
};

export default AchievementsOverview;
